import React from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type PressableProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { tokens } from '../tokens';

export interface ButtonProps
  extends Omit<PressableProps, 'style' | 'children'> {
  /**
   * Button text content
   */
  children?: string | React.ReactNode;

  /**
   * Button visual style
   * - rectangular: Sharp corners (2px radius)
   * - rounded: Pill shape (99px radius)
   * @default 'rounded'
   */
  variant?: 'rectangular' | 'rounded';

  /**
   * Button size
   * - default: 32px height
   * - large: 48px height
   * @default 'default'
   */
  size?: 'default' | 'large';

  /**
   * Button state style
   * - default: Light gray background
   * - active: Dark background with white text (primary)
   * - warning: Error/warning color background
   * - outline: Transparent with colored text and border
   * @default 'default'
   */
  state?: 'default' | 'active' | 'warning' | 'outline';

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;

  /**
   * Loading state - shows activity indicator
   * @default false
   */
  loading?: boolean;

  /**
   * Icon to show on the left side
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to show on the right side
   */
  rightIcon?: React.ReactNode;

  /**
   * Custom container style
   */
  style?: ViewStyle;

  /**
   * Custom text style
   */
  textStyle?: TextStyle;

  /**
   * Accessible label for screen readers
   */
  accessibilityLabel?: string;
}

/**
 * Button Component
 *
 * A flexible button component with multiple variants, sizes, and states.
 * Extracted and verified from Figma Crop Circle System.
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button state="active" onPress={handlePress}>
 *   Get Started
 * </Button>
 *
 * // Pill button with icon
 * <Button variant="rounded" rightIcon={<ArrowRight />}>
 *   Continue
 * </Button>
 *
 * // Warning button
 * <Button state="warning" size="large">
 *   Delete
 * </Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'rounded',
  size = 'default',
  state = 'default',
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  style,
  textStyle,
  accessibilityLabel,
  ...pressableProps
}) => {
  // Determine current state for styling
  const isDisabled = disabled || loading;
  const effectiveState = isDisabled ? 'disabled' : state;

  // Get container style based on variant, size, and state
  const containerStyle = getContainerStyle(variant, size, effectiveState);
  const textColor = getTextColor(effectiveState);

  // Use accessibilityLabel if provided, otherwise fallback to children only if it's a string
  const effectiveAccessibilityLabel =
    accessibilityLabel || (typeof children === 'string' ? children : undefined);

  return (
    <Pressable
      {...pressableProps}
      disabled={isDisabled}
      accessibilityLabel={effectiveAccessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled }}
      style={({ pressed }) => [
        styles.container,
        containerStyle,
        pressed && !isDisabled && styles.pressed,
        style,
      ]}
    >
      <View style={styles.content}>
        {loading ? (
          <ActivityIndicator
            size="small"
            color={textColor}
            style={styles.loader}
          />
        ) : (
          <>
            {leftIcon && <View style={styles.iconLeft}>{leftIcon}</View>}
            {children && (
              <Text
                style={[styles.text, { color: textColor }, textStyle]}
                numberOfLines={1}
              >
                {children}
              </Text>
            )}
            {rightIcon && <View style={styles.iconRight}>{rightIcon}</View>}
          </>
        )}
      </View>
    </Pressable>
  );
};

/**
 * Get container style based on variant, size, and state
 */
function getContainerStyle(
  variant: 'rectangular' | 'rounded',
  size: 'default' | 'large',
  state: 'default' | 'active' | 'warning' | 'outline' | 'disabled'
): ViewStyle {
  const style: ViewStyle = {
    // Border radius based on variant
    borderRadius: variant === 'rounded' ? tokens.borderRadius.pill : 2,

    // Height based on size
    height: size === 'large' ? 48 : 32,

    // Padding based on size
    paddingHorizontal: size === 'large' ? tokens.spacing.lg : tokens.spacing.md,
  };

  // Background and border based on state
  switch (state) {
    case 'active':
      style.backgroundColor = tokens.colors.neutral[800]; // Very dark gray
      break;

    case 'warning':
      style.backgroundColor = tokens.colors.orange[500]; // rgb(226,77,42)
      break;

    case 'outline':
      style.backgroundColor = 'transparent';
      style.borderWidth = 1;
      style.borderColor = tokens.colors.orange[500]; // rgb(226,77,42)
      break;

    case 'disabled':
      style.backgroundColor = tokens.colors.slate[100]; // #ECEEF1 - matches Figma Disable state
      break;

    case 'default':
    default:
      style.backgroundColor = tokens.colors.slate[100]; // #ECEEF1 - matches Figma Default state
      break;
  }

  return style;
}

/**
 * Get text color based on state
 */
function getTextColor(
  state: 'default' | 'active' | 'warning' | 'outline' | 'disabled'
): string {
  switch (state) {
    case 'active':
    case 'warning':
      return tokens.colors.neutral[100]; // rgb(252,252,252) - white text

    case 'outline':
      return tokens.colors.orange[600]; // rgb(196,74,10)

    case 'disabled':
      return tokens.colors.neutral[300]; // #D8D8D8 - matches Figma Disable text

    case 'default':
    default:
      return tokens.colors.system.textPrimary; // Default text color
  }
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 76, // From Figma specs
  },

  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs, // 4px between icon and text
  },

  text: {
    ...tokens.typography.body.medium, // 14px Inter Regular
    textAlign: 'center',
  },

  iconLeft: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },

  iconRight: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },

  loader: {
    marginHorizontal: tokens.spacing.xs,
  },

  pressed: {
    opacity: 0.8,
  },
});

Button.displayName = 'Button';
